import { CURRENCY_PATTERNS } from './display-currency-patterns';

/**
 * Display prices, formatted the same on the server and in every browser.
 *
 * WHY NOT Intl.NumberFormat WITH THE CURRENCY'S LOCALE. That is what this did
 * until 2026-10-07, and it was safe only because the server never formatted a
 * converted price: every page arrived priced in AUD and switched to the
 * visitor's currency once the browser had fetched exchange rates, which is the
 * flash the rates now arriving with the page removes. Once the server prints a
 * euro price, the browser has to print the same characters while hydrating,
 * and Intl's output depends on the CLDR data each engine ships: a Swiss
 * negative, the South African separators, the direction marks in a shekel or
 * dirham price, a narrow or a full no-break space. Node, Chrome, Firefox and
 * Safari do not ship the same version, and since nothing wraps the app in a
 * Suspense boundary, one differing character makes React redraw the whole page.
 *
 * So each currency's pattern (symbol and spacing before and after the number,
 * for positive and negative amounts, separators, grouping, decimals) is a fixed
 * table, display-currency-patterns.ts, taken from the server's Intl, and the
 * only thing asked of the runtime is plain en-US digits with no grouping, which
 * every engine has printed identically for as long as Intl has existed.
 * display-currency-format.test.ts checks the table against Intl and that the
 * formatter asks Intl for nothing more. Regenerate the table after a Node or
 * ICU upgrade with:
 *
 *   node --experimental-strip-types --import ./scripts/register-ts-resolve.mjs scripts/gen-currency-patterns.mjs
 */

/** The locale each display currency is written in, for its symbol placement and separators. */
export const CURRENCY_LOCALE: Record<string, string> = {
  USD: 'en-US', EUR: 'de-DE', GBP: 'en-GB', JPY: 'ja-JP',
  AUD: 'en-AU', CAD: 'en-CA', CHF: 'de-CH', CNY: 'zh-CN',
  INR: 'en-IN', BRL: 'pt-BR', ZAR: 'en-ZA', SGD: 'en-SG',
  HKD: 'zh-HK', NZD: 'en-NZ', SEK: 'sv-SE', NOK: 'nb-NO',
  DKK: 'da-DK', MXN: 'es-MX', TRY: 'tr-TR', RUB: 'ru-RU',
  PLN: 'pl-PL', MYR: 'ms-MY', THB: 'th-TH', IDR: 'id-ID',
  CZK: 'cs-CZ', HUF: 'hu-HU', PHP: 'fil-PH', RON: 'ro-RO',
  ILS: 'he-IL', AED: 'ar-AE',
};

/** How one currency is written, as its locale's Intl writes it. */
export type CurrencyPattern = {
  /** The locale the pattern was taken from. */
  locale: string;
  /** Everything before and after the digits of a positive amount: symbol, spaces, direction marks. */
  prefix: string;
  suffix: string;
  /** The same for a negative amount, minus sign included. Not always the positive pattern plus a sign. */
  negativePrefix: string;
  negativeSuffix: string;
  decimal: string;
  group: string;
  /** Digits in the last group, and in each group before it (3 and 3; India's lakh grouping is 3 and 2). */
  primaryGrouping: number;
  secondaryGrouping: number;
  /** Digits that must stand before the first separator for any to appear (2 where 1234 stays ungrouped). */
  minimumGroupingDigits: number;
  fractionDigits: number;
};

const NUMERIC_PARTS = new Set(['integer', 'group', 'decimal', 'fraction']);

/**
 * Read a currency's pattern off this runtime's Intl. Used to generate the
 * table and, in the test, to check it; never at render time.
 */
export function derivePattern(currency: string, locale: string): CurrencyPattern {
  const nf = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'symbol' });
  const read = (value: number) => {
    const parts = nf.formatToParts(value);
    const first = parts.findIndex((part) => NUMERIC_PARTS.has(part.type));
    let last = first;
    parts.forEach((part, index) => { if (NUMERIC_PARTS.has(part.type)) last = index; });
    return {
      before: parts.slice(0, first).map((part) => part.value).join(''),
      after: parts.slice(last + 1).map((part) => part.value).join(''),
      integers: parts.filter((part) => part.type === 'integer').map((part) => part.value),
      group: parts.find((part) => part.type === 'group')?.value ?? '',
      decimal: parts.find((part) => part.type === 'decimal')?.value ?? '',
    };
  };
  const millions = read(1234567.891);
  const negative = read(-1234567.891);
  const integers = millions.integers;
  const primaryGrouping = integers.length > 1 ? integers[integers.length - 1].length : 3;
  const secondaryGrouping = integers.length > 2 ? integers[integers.length - 2].length : primaryGrouping;
  const minimumGroupingDigits =
    read(1234).integers.length > 1 ? 1 : read(12345).integers.length > 1 ? 2 : 3;
  return {
    locale,
    prefix: millions.before,
    suffix: millions.after,
    negativePrefix: negative.before,
    negativeSuffix: negative.after,
    // A currency with no minor unit (the yen) never prints one.
    decimal: millions.decimal || '.',
    group: millions.group,
    primaryGrouping,
    secondaryGrouping,
    minimumGroupingDigits,
    fractionDigits: nf.resolvedOptions().maximumFractionDigits ?? 2,
  };
}

// One plain-digit formatter per number of decimals, made on first use.
const digitFormatters = new Map<number, Intl.NumberFormat>();

/** "1234567.89": rounded as Intl rounds a currency, with no grouping and no symbol. */
function plainDigits(value: number, fractionDigits: number): string {
  let nf = digitFormatters.get(fractionDigits);
  if (!nf) {
    nf = new Intl.NumberFormat('en-US', {
      useGrouping: false,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    digitFormatters.set(fractionDigits, nf);
  }
  return nf.format(value);
}

function isDigits(text: string): boolean {
  if (text.length === 0) return false;
  for (const ch of text) {
    if (ch < '0' || ch > '9') return false;
  }
  return true;
}

function groupInteger(integer: string, pattern: CurrencyPattern): string {
  // NaN and Infinity come through as words, and are never grouped.
  if (!pattern.group || !isDigits(integer)) return integer;
  if (integer.length < pattern.primaryGrouping + pattern.minimumGroupingDigits) return integer;
  const groups = [integer.slice(-pattern.primaryGrouping)];
  let rest = integer.slice(0, -pattern.primaryGrouping);
  while (rest.length > pattern.secondaryGrouping) {
    groups.unshift(rest.slice(-pattern.secondaryGrouping));
    rest = rest.slice(0, -pattern.secondaryGrouping);
  }
  groups.unshift(rest);
  return groups.join(pattern.group);
}

/** Format an amount in a pattern. Negative zero takes the negative pattern, as Intl does. */
export function formatWithPattern(amount: number, pattern: CurrencyPattern): string {
  const negative = amount < 0 || Object.is(amount, -0);
  const digits = plainDigits(Math.abs(amount), pattern.fractionDigits);
  const point = digits.indexOf('.');
  const integer = point === -1 ? digits : digits.slice(0, point);
  const fraction = point === -1 ? '' : digits.slice(point + 1);
  const number = groupInteger(integer, pattern) + (fraction ? pattern.decimal + fraction : '');
  return negative
    ? pattern.negativePrefix + number + pattern.negativeSuffix
    : pattern.prefix + number + pattern.suffix;
}

/**
 * An amount ALREADY in `currency`, written the way that currency's locale
 * writes it. Null for a currency the table does not know, so the caller can
 * fall back to Intl (which is only hydration-safe off the server render).
 */
export function formatDisplayCurrency(amount: number, currency: string): string | null {
  const pattern = CURRENCY_PATTERNS[currency];
  return pattern ? formatWithPattern(amount, pattern) : null;
}
